import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/comics';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const slide = heroSlides[current];

  return (
    <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden">
      {heroSlides.map((s, idx) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            idx === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.bg}
            alt={s.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-xl" key={slide.id}>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-300 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full mb-4 whitespace-nowrap">
              {slide.subtitle}
            </span>
            <h2 className="text-3xl md:text-5xl font-heading text-white mb-3 leading-tight">
              {slide.title}
            </h2>
            <p className="text-sm md:text-base text-white/70 mb-6 leading-relaxed max-w-md">
              {slide.description}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to={`/comic/${slide.comicId}`}
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 dark:text-foreground-950 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即阅读
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
              <Link
                to={`/comic/${slide.comicId}`}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 px-5 py-2.5 rounded-full text-sm font-medium hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看详情
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              idx === current
                ? 'w-6 bg-primary-500'
                : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`切换到第${idx + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}